/*
 * Merchant: in the Manifest's full-counter grid (the one with the Modern
 * Equipment/Other/Surplus/etc. department chips), only show items whose own
 * product photo is a plain studio shot on a blank/white background — hide
 * anything whose photo shows the item in a lifestyle setting (worn, staged,
 * outdoors, colored backdrop).
 *
 * The scoring itself (sample a handful of corner patches, look for the
 * bright/low-variance signature of a real seamless backdrop) is the exact
 * same calibrated approach assets/sat-photo-picker.js already uses to pick a
 * studio shot over a lifestyle one for Oscar4Fox's pattern rail — duplicated
 * here rather than called through window.SATPhotoPicker.choose(), because
 * that function answers a different question ("is there a BETTER photo than
 * the merchant's default?") and stays silent whenever the default already
 * wins, so it can't tell us whether a product has ANY qualifying photo at
 * all, only whether one beats the current pick. Same WIN_THRESHOLD (20),
 * same real calibration data that threshold is based on.
 *
 * Scoring requires every candidate image to actually load, which is
 * inherently async and real work across however many items are in the
 * active department — too slow to block the grid on. So this defaults to
 * SHOWING every item with its existing photo (identical to today), scores
 * each one in the background, and only pulls an item back out once it's
 * confirmed to have no qualifying plain-background photo — never an empty
 * flash of "Nothing On The Counter" while scoring is still in flight, just
 * a grid that quietly tightens up as results come in. An item that already
 * has a qualifying photo just keeps it; this never swaps away a photo the
 * merchant already set that happens to pass.
 *
 * Scoped to the Manifest page only (state.page === 'manifest') — the
 * Southern Air Import's own curated picks (assets/sat-issue-plan-patch.js)
 * and the home page's "Featured This Week" row keep whatever photo they
 * already use untouched.
 *
 * willReadFrequently: this scores every product on the Manifest (currently
 * ~480), each against up to 6 candidate images, each sampled via 5
 * getImageData() calls on a freshly-created canvas — thousands of readbacks
 * that were flooding the console with Chrome's "Multiple readback
 * operations... faster with willReadFrequently" warning, a real perf signal
 * (forces a slower GPU/CPU sync path) not just console noise. Set once at
 * context creation, no behavior change.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }

  var scoreCache = {}; // productId -> winning url, or null if nothing qualified
  var pending = {};

  function loadImg(url) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { resolve(null); };
      img.src = url;
    });
  }

  function sample(img) {
    try {
      var w = 48, h = 48;
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      var patchSize = 7;
      var patches = [
        [0, 0], [w - patchSize, 0], [0, h - patchSize], [w - patchSize, h - patchSize],
        [Math.floor(w / 2 - patchSize / 2), 0]
      ];
      var sum = 0, sumSq = 0, n = 0;
      patches.forEach(function (xy) {
        var d = ctx.getImageData(xy[0], xy[1], patchSize, patchSize).data;
        for (var i = 0; i < d.length; i += 4) {
          var lum = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
          sum += lum; sumSq += lum * lum; n++;
        }
      });
      var mean = sum / n;
      var variance = sumSq / n - mean * mean;
      return { brightness: mean, variance: variance };
    } catch (e) {
      return null; // canvas read blocked (CORS, decode failure) — treat as no signal
    }
  }

  function score(stats) {
    if (!stats) return -1;
    var brightnessScore = Math.max(0, stats.brightness - 195) * 1.8;
    var variancePenalty = Math.min(stats.variance / 5, 90);
    return brightnessScore - variancePenalty;
  }

  var WIN_THRESHOLD = 20; // same calibration as assets/sat-photo-picker.js

  function scoreProduct(self, p) {
    var id = p.id;
    pending[id] = true;
    var urls = ((p.images && p.images.length) ? p.images : (p.photo ? [p.photo] : [])).slice(0, 6);
    if (!urls.length) {
      scoreCache[id] = null;
      pending[id] = false;
      self.setState({ _whiteBgTick: (self.state._whiteBgTick || 0) + 1 });
      return;
    }
    Promise.all(urls.map(loadImg)).then(function (imgs) {
      pending[id] = false;
      var bestScore = -1, bestUrl = null;
      imgs.forEach(function (img, i) {
        if (!img) return;
        var s = score(sample(img));
        if (s > bestScore) { bestScore = s; bestUrl = urls[i]; }
      });
      scoreCache[id] = bestScore >= WIN_THRESHOLD ? bestUrl : null;
      self.setState({ _whiteBgTick: (self.state._whiteBgTick || 0) + 1 });
    });
  }

  var proto = window.SATComponent.prototype;
  var origVisibleProducts = proto.visibleProducts;
  proto.visibleProducts = function () {
    var self = this;
    var list = origVisibleProducts.call(this);
    if (this.state.page !== 'manifest') { return list; }

    var out = [];
    list.forEach(function (p) {
      if (p.id in scoreCache) {
        var winner = scoreCache[p.id];
        if (winner) { out.push(winner === p.photo ? p : Object.assign({}, p, { photo: winner })); }
        return; // scored, no qualifying plain-background photo — excluded
      }
      if (!pending[p.id]) { scoreProduct(self, p); }
      out.push(p); // not yet scored — shown as-is until proven otherwise
    });
    return out;
  };
})();
